import { CARD_PROVIDER_LABELS, CASH_DOC_LABELS, CASH_REASON_LABELS, type BankCode, type CashDoc, type CashMovement, type Expense, type Shift } from './mock-data'
import { parseMoneyInput } from './money-input'

/**
 * 119 — what stays in the drawer at close, and where the rest went.
 *
 * The owner closed with ₾88.20 counted and opened the next shift with ₾20;
 * the ₾68.20 between them was written nowhere (2026-09-27). Now the close
 * says what is LEFT for the next shift and why the rest left, and the next
 * open is compared with it by the database (`opening_carried`). These are
 * the rules both dialogs, the Z-report, the journal and the morning mail
 * read — one copy, pinned by drawer-float.test.ts.
 */

export type TakeoutReason = 'owner' | 'bank' | 'other'

/** Where the money went — close_shift (119) accepts exactly these. */
export const TAKEOUT_LABELS: Record<TakeoutReason, string> = {
  owner: 'მფლობელს',
  bank: 'ბანკში',
  other: 'სხვა',
}

/** The owner's standing float (settings: floatKeep / floatAmount). */
export type FloatSetting = { keep: boolean; amount: number }

const r2 = (n: number) => Math.round(n * 100) / 100
const EPS = 0.005

/**
 * What a closed shift left in the drawer: its `closing_float` since 119;
 * before 119 nothing was taken at close, so the whole count stayed.
 */
export function leftInDrawer(s: Pick<Shift, 'closing_cash' | 'closing_float'>): number | null {
  if (s.closing_float != null) return s.closing_float
  return s.closing_cash
}

/** Taken out at close (0 when nothing, or for a shift closed before 119).
 *  137: the cashier's salary left the count too — it is not „taken". */
export function takenAtClose(s: Pick<Shift, 'closing_cash' | 'closing_float' | 'closing_salary'>): number {
  if (s.closing_float == null || s.closing_cash == null) return 0
  return Math.max(0, r2(s.closing_cash - s.closing_float - (s.closing_salary ?? 0)))
}

/**
 * What the close dialog proposes to leave: the owner's float when it is on
 * (never more than was counted), otherwise everything — as before 119.
 */
export function proposedFloat(counted: number, setting: FloatSetting): number {
  const c = Math.max(0, r2(counted))
  if (setting.keep && setting.amount > 0) return Math.min(r2(setting.amount), c)
  return c
}

// ── 137 — the cashier's daily salary, paid at close ──────────────────────────

/** The shop's salary setting (settings: salaryKeep / salaryAmount). */
export type SalarySetting = { keep: boolean; amount: number }

/** A stored `salaryAmount` → ₾ to the tetri; junk, empty or negative is ₾0. */
export function parseSalaryAmount(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN
  return Number.isFinite(n) && n > 0 ? r2(n) : 0
}

/** Salary already paid from THIS shift's drawer (a 107 expense, category salary). */
export function salaryPaidThisShift(
  shiftId: string,
  moves: Pick<CashMovement, 'id' | 'shift_id'>[],
  expenses: Pick<Expense, 'category' | 'cash_movement_id' | 'amount'>[],
): number {
  const ids = new Set(moves.filter((m) => m.shift_id === shiftId).map((m) => m.id))
  return r2(expenses.filter((e) => e.category === 'salary' && e.cash_movement_id && ids.has(e.cash_movement_id))
    .reduce((a, e) => a + e.amount, 0))
}

/** 143 — salary already paid TODAY to this shift's cashier, in any shift: a
 *  close's own expense (`shift_id`) and any payout from a drawer mid-shift
 *  (`cash_movement_id`), on the shop's business day. close_shift holds the
 *  same rule since 143 (it was this shift only, so closing and reopening paid
 *  the daily amount again). `cashierId` null = the owner's own shifts. */
export function salaryPaidToday(
  cashierId: string | null | undefined,
  businessDay: string,
  shifts: { id: string; cashier_id?: string | null }[],
  moves: Pick<CashMovement, 'id' | 'shift_id'>[],
  expenses: Pick<Expense, 'category' | 'cash_movement_id' | 'amount' | 'expense_date' | 'shift_id'>[],
): number {
  const mine = new Set(shifts.filter((s) => (s.cashier_id ?? null) === (cashierId ?? null)).map((s) => s.id))
  const moveShift = new Map(moves.map((m) => [m.id, m.shift_id]))
  return r2(expenses.filter((e) => {
    if (e.category !== 'salary' || e.expense_date !== businessDay) return false
    const shiftId = e.shift_id ?? (e.cash_movement_id ? moveShift.get(e.cash_movement_id) : null)
    return !!shiftId && mine.has(shiftId)
  }).reduce((a, e) => a + e.amount, 0))
}

/** What a close may pay without the owner's PIN — close_shift (137) holds the same. */
export function salaryCeiling(setting: SalarySetting, paid: number): number {
  if (!setting.keep || !(setting.amount > 0)) return 0
  return Math.max(0, r2(setting.amount - paid))
}

/** What the close dialog proposes: the ceiling, never more than was counted. */
export function proposedSalary(counted: number, setting: SalarySetting, paid: number): number {
  return Math.min(salaryCeiling(setting, paid), Math.max(0, r2(counted)))
}

/** 143 — the ceiling the server named, for THIS shift on THIS business day
 *  only: a close refused before the day turned must not carry yesterday's
 *  lower ceiling into a close after it (Codex's review of 1.23). */
export type SalaryHint = { shiftId: string; day: string; limit: number }
export function salaryHintFor(hint: SalaryHint | null | undefined, shiftId: string, day: string): number | null {
  return hint && hint.shiftId === shiftId && hint.day === day ? hint.limit : null
}

/** The ceiling close_shift names in `SALARY_LIMIT:<n>`; null when it is not that refusal. */
export function salaryLimitFrom(message: string): number | null {
  const m = /SALARY_LIMIT:([0-9]+(?:\.[0-9]+)?)/.exec(message)
  return m ? Number(m[1]) : null
}

/** What the close dialog has in hand (137): the count, the two settings, what
 *  this shift already paid as salary, the ceiling the server named (if it
 *  did), and the two fields — null while untouched. */
export type CloseSplitInput = {
  counted: number
  salarySetting: SalarySetting
  paidSalary: number
  hint: number | null
  salaryTyped: string | null
  floatSetting: FloatSetting
  leftTyped: string | null
}

/** Where the counted cash goes at close: salary first, then the float, the rest taken. */
export function closeSplit(i: CloseSplitInput) {
  const ceiling = i.hint ?? salaryCeiling(i.salarySetting, i.paidSalary)
  const showSalary = i.salarySetting.keep && i.salarySetting.amount > 0
  const counted = Math.max(0, i.counted)
  // A proposal never passes the count, not even by a tetri (close_shift
  // refuses a salary above the count): rounded DOWN.
  const salary = !showSalary ? 0
    : i.salaryTyped != null ? parseMoneyInput(i.salaryTyped)
    : Math.floor(Math.min(ceiling, counted) * 100 + 1e-6) / 100
  const afterSalary = Math.max(0, Math.round((counted - salary) * 100) / 100)
  const proposedLeft = proposedFloat(afterSalary, i.floatSetting)
  const left = i.leftTyped != null ? Math.max(0, Math.round((parseFloat(i.leftTyped) || 0) * 100) / 100) : proposedLeft
  const take = Math.max(0, Math.round((afterSalary - left) * 100) / 100)
  // The spec: the 119 reminder still fires when the salary eats the change —
  // measured against what was COUNTED, not what the salary left.
  const remind = needsFloatReminder(counted, left, i.floatSetting)
  return { showSalary, ceiling, salary, afterSalary, proposedLeft, left, take, remind }
}

/**
 * Remind before closing? Only when the owner keeps a float and less than it
 * is being left although the drawer holds it. A reminder, never a refusal —
 * the owner said taking it all must stay possible.
 */
export function needsFloatReminder(counted: number, left: number, setting: FloatSetting): boolean {
  if (!setting.keep || setting.amount <= 0) return false
  return left + EPS < Math.min(r2(setting.amount), Math.max(0, r2(counted)))
}

/** From this far off the expected cash, either way, the close asks first. */
export const COUNT_GAP_ASK = 50

/**
 * Ask „is this count right?" before closing? When the counted cash is
 * ₾50 or more away from the expected — over or under. SAMGORI's 09-29
 * close typed 5337 for 533.70 and nothing stopped it (audit, 2026-10-02).
 * A question, never a refusal: a real shortage still closes.
 */
export function bigCountGap(counted: number, expected: number): boolean {
  return Math.abs(r2(counted) - r2(expected)) + EPS >= COUNT_GAP_ASK
}

/**
 * The gap at opening: what was put in the drawer minus what the previous
 * shift left. Null when there is nothing to compare (the first shift, or a
 * shift opened before 119). Negative = money missing between the shifts.
 */
export function openingGap(s: Pick<Shift, 'opening_cash' | 'opening_carried'>): number | null {
  if (s.opening_carried == null) return null
  const g = r2(s.opening_cash - s.opening_carried)
  return Math.abs(g) < EPS ? 0 : g
}

// ── 120 — which bank ─────────────────────────────────────────────────────────

/** A take-out's words, with the bank when one was named: „ბანკში (TBC)". */
export function takeoutLabel(reason: TakeoutReason | null | undefined, bank?: BankCode | null): string {
  const r = reason ?? 'other'
  return r === 'bank' && bank ? `${TAKEOUT_LABELS.bank} (${CARD_PROVIDER_LABELS[bank]})` : TAKEOUT_LABELS[r]
}

/** A drawer movement's words: „ბანკში შეტანა (TBC)". */
export function movementLabel(m: Pick<CashMovement, 'reason' | 'bank' | 'doc'>): string {
  if (m.doc) return CASH_DOC_LABELS[m.doc]
  const base = CASH_REASON_LABELS[m.reason]
  return m.reason === 'bank' && m.bank ? `${base} (${CARD_PROVIDER_LABELS[m.bank]})` : base
}

/** The words 170 / 171 write before the supplier in a document's note — for a 1.53 till, which shows „სხვა — <note>". */
const DOC_NOTE_PREFIX: Record<CashDoc, string> = { supplier_return: 'დაბრუნება — ', purchase: 'მიღება — ' }

/**
 * A movement's note as 1.54 shows it beside its label: a document's note
 * without the word its label already says („მომწოდებლის დაბრუნება — ნიკორა",
 * not „… — დაბრუნება — ნიკორა"); a hand-written note as it was typed.
 */
export function movementNote(m: Pick<CashMovement, 'doc' | 'note'>): string | null {
  if (!m.note) return null
  const prefix = m.doc ? DOC_NOTE_PREFIX[m.doc] : null
  return prefix && m.note.startsWith(prefix) ? m.note.slice(prefix.length) || null : m.note
}

/** The bank named most recently — offered first next time (a shop mostly
 *  carries its cash to one). */
export function lastBank(
  shifts: Pick<Shift, 'takeout_bank' | 'closed_at'>[],
  moves: Pick<CashMovement, 'bank' | 'created_at'>[],
): BankCode | null {
  let best: { at: string; bank: BankCode } | null = null
  for (const s of shifts) {
    if (s.takeout_bank && s.closed_at && (!best || s.closed_at > best.at)) best = { at: s.closed_at, bank: s.takeout_bank }
  }
  for (const m of moves) {
    if (m.bank && (!best || m.created_at > best.at)) best = { at: m.created_at, bank: m.bank }
  }
  return best?.bank ?? null
}

export type OutflowKey = 'owner' | 'bank_tbc' | 'bank_bog' | 'bank_liberty' | 'bank_unnamed' | 'salary' | 'expense' | 'purchase' | 'other'

const OUTFLOW_ORDER: OutflowKey[] = ['owner', 'bank_tbc', 'bank_bog', 'bank_liberty', 'bank_unnamed', 'salary', 'expense', 'purchase', 'other']
/** The morning mail's words (Georgian); the screen says `outflowText` (drawer-text). */
export const OUTFLOW_LABELS: Record<OutflowKey, string> = {
  owner: 'მფლობელს',
  bank_tbc: 'ბანკში — TBC',
  bank_bog: 'ბანკში — BOG',
  bank_liberty: 'ბანკში — Liberty',
  bank_unnamed: 'ბანკში — ბანკი არ არის მითითებული',
  salary: 'ხელფასი (ცვლის დახურვისას)',
  expense: 'ხარჯი (სალაროდან)',
  purchase: 'მომწოდებელს (მიღებისას)',
  other: 'სხვა',
}

/**
 * Cash that LEFT the drawer, by where it went: take-outs at close plus
 * mid-shift withdrawals. Never takings — a card sale's bank is a terminal,
 * this is a deposit, and the owner asked that the two never mix (2026-09-27).
 * The caller decides the period and the branch.
 */
export function drawerOutflows(
  shifts: Pick<Shift, 'closing_cash' | 'closing_float' | 'closing_salary' | 'takeout_reason' | 'takeout_bank'>[],
  moves: Pick<CashMovement, 'direction' | 'amount' | 'reason' | 'bank' | 'doc'>[],
): { total: number; rows: { key: OutflowKey; label: string; amount: number }[] } {
  const sums = new Map<OutflowKey, number>()
  const add = (k: OutflowKey, v: number) => { if (v > 0) sums.set(k, (sums.get(k) ?? 0) + v) }
  const bankKey = (b?: BankCode | null): OutflowKey => (b ? (`bank_${b}` as OutflowKey) : 'bank_unnamed')
  for (const s of shifts) {
    if (s.closing_cash != null && s.closing_float != null) add('salary', s.closing_salary ?? 0)
    const t = takenAtClose(s)
    if (t <= 0) continue
    add(s.takeout_reason === 'bank' ? bankKey(s.takeout_bank) : s.takeout_reason === 'owner' ? 'owner' : 'other', t)
  }
  for (const m of moves) {
    if (m.direction !== 'out') continue
    add(m.doc === 'purchase' ? 'purchase' : m.reason === 'bank' ? bankKey(m.bank) : m.reason === 'owner' ? 'owner' : m.reason === 'expense' ? 'expense' : 'other', m.amount)
  }
  const rows = OUTFLOW_ORDER.filter((k) => (sums.get(k) ?? 0) > 0).map((k) => ({ key: k, label: OUTFLOW_LABELS[k], amount: r2(sums.get(k)!) }))
  return { total: r2(rows.reduce((a, r) => a + r.amount, 0)), rows }
}
